"use client";

import { useTranslations } from "next-intl";
import { AlertCircle, RefreshCw, X } from "lucide-react";
import { Button } from "@/shared/presentation/components/ui";
import { cn } from "@/shared/core/utils";

interface ErrorDisplayProps {
  error?: string | null;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: "inline" | "toast" | "fullscreen";
}

/**
 * Error display component for showing error messages
 * Replicates the old project's ErrorDisplay component with enhanced features
 */
export function ErrorDisplay({
  error,
  title,
  onRetry,
  onDismiss,
  className,
  variant = "toast",
}: ErrorDisplayProps) {
  const t = useTranslations("errors");

  if (!error) return null;

  const errorTitle = title || t("generic");

  if (variant === "fullscreen") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div
          className={cn(
            "flex max-w-md flex-col items-center gap-4 rounded-lg bg-card p-8 shadow-lg",
            className
          )}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {errorTitle}
          </h2>
          <p className="text-center text-muted-foreground">{error}</p>
          <div className="flex gap-3">
            {onRetry && (
              <Button onClick={onRetry} variant="default">
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("tryAgain")}
              </Button>
            )}
            {onDismiss && (
              <Button onClick={onDismiss} variant="outline">
                {t("dismiss")}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-4",
          className
        )}
      >
        <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
        <div className="flex-1">
          <p className="text-sm font-medium text-destructive">{errorTitle}</p>
          <p className="text-sm text-destructive/80">{error}</p>
        </div>
        {onRetry && (
          <Button
            onClick={onRetry}
            variant="ghost"
            size="sm"
            className="text-destructive"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
        {onDismiss && (
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  // Toast variant (default)
  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 flex max-w-sm items-start gap-3 rounded-lg border border-destructive/50 bg-card p-4 shadow-lg",
        className
      )}
    >
      <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{errorTitle}</p>
        <p className="text-sm text-muted-foreground">{error}</p>
        {onRetry && (
          <Button
            onClick={onRetry}
            variant="link"
            size="sm"
            className="mt-2 h-auto p-0 text-primary"
          >
            {t("tryAgain")}
          </Button>
        )}
      </div>
      {onDismiss && (
        <Button
          onClick={onDismiss}
          variant="ghost"
          size="icon"
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default ErrorDisplay;
